import React, { useContext, useEffect, useState } from "react";
import SearchBar from "@/components/navbars/SearchBar";
import { colorStyles } from "@/utils/constants/constants";
import FilterOverlay from "@/components/overlays/FilterOverlay";
import { Button } from "@/components/ui/button";
import OwnershipSelectCheckbox from "@/components/buttons/OwnershipSelectCheckbox";
import { fetchConfig } from "@/services/setting.service";
import { OrganizationContext } from "@/context/providers/OrganizationProvider";

interface TableHeadProps {
    search?: (searchText: string) => void;
    reset?: () => void;
    title: string;
    pageNo: number;
    dataLength: number;
    groups?: any;
    roles?: any;
    setDataLength: (dataLength: number) => void;
    button?: React.ReactNode;
    handleApplyFilter?: (filterOverride?: any) => void;
    handleReset?: () => void;
    showFilter?: boolean;
    filter?: any;
    setFilter?: (filter: any) => void;
    options?: any;
    setPageNo?: (pageNo: number) => void;
    currentEnvo?: number;
    totalEnvo?: number;
    id: string;
    searchItem?: string | null;
    /** Multiselect delete/transfer pattern (matches user portal). */
    selectedCount?: number;
    multiselectAction?: React.ReactNode;
    showMultiselectSelectAll?: boolean;
    selectAllChecked?: boolean | "indeterminate";
    onSelectAllChange?: () => void;
}

function TableHead(props: TableHeadProps) {
    const { organization } = useContext(OrganizationContext);
    const [showSearchBar, setShowSearchBar] = useState(false);
    const [isDocumentSearchEnabled, setIsDocumentSearchEnabled] = useState(
        () => localStorage.getItem("isDocumentSearchEnabled") === "true"
    );

    const filterTitles = new Set(["Admins", "Users", "Envelopes", "Templates", "Forms", "Web Forms"]);
    const showFilterOverlay = props.showFilter ?? filterTitles.has(props.title);
    const selectedCount = props.selectedCount ?? 0;
    const showSelectionToolbar = selectedCount > 0;
    const displayTitle = showSelectionToolbar ? `${selectedCount} Selected` : props.title;

    useEffect(() => {
        setShowSearchBar(false);
        fetchConfig(
            organization?.organization ? { id: organization.organization } : null,
            (res: any) => {
                const enabled = !!(
                    res?.is_document_search_enabled ??
                    res?.configurations?.is_document_search_enabled
                );
                setIsDocumentSearchEnabled(enabled);
                localStorage.setItem("isDocumentSearchEnabled", String(enabled));
            },
            () => {}
        );
    }, [organization?.organization]);

    return showSearchBar ? (
        <SearchBar
            id={props.id}
            onHide={() => setShowSearchBar(false)}
            handleSearch={props.search}
            reset={props.reset}
            title={props.title}
            documentSearchEnabled={isDocumentSearchEnabled}
            activeSearch={props.searchItem}
            leadingContent={
                showSelectionToolbar ? (
                    <span className="gs-list-page-selection-count" id={`${props.id}-title`}>
                        {displayTitle}
                    </span>
                ) : null
            }
            trailingContent={showSelectionToolbar ? props.multiselectAction : null}
        />
    ) : (
        <div className="gs-list-page-header">
            <div className="gs-list-page-title-wrap">
                {props.showMultiselectSelectAll ? (
                    <span className="gs-list-page-mobile-select-all">
                        <OwnershipSelectCheckbox
                            id={`${props.id}-select-all-header`}
                            checked={props.selectAllChecked ?? false}
                            onCheckedChange={() => props.onSelectAllChange?.()}
                            aria-label="Select all on this page"
                        />
                    </span>
                ) : null}
                <h5
                    className={`gs-list-page-title${showSelectionToolbar ? " gs-list-page-title--selection" : ""}`}
                    id={`${props.id}-title`}
                >
                    {displayTitle}
                </h5>
                <span className="gs-list-page-count" id={`${props.id}-data-count`}>
                    {!showSelectionToolbar && props.currentEnvo
                        ? `${props.currentEnvo} out of ${props.totalEnvo}`
                        : null}
                </span>
            </div>
            <div className="gs-list-page-tools">
                {props.title !== "Roles" && props.title !== "Groups" && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowSearchBar(true)}
                        id={`${props.id}-search-btn`}
                    >
                        <em
                            id={`${props.id}-search-icon`}
                            className="icon ni ni-search"
                        />
                    </Button>
                )}
                {props.button && <div>{props.button}</div>}
                {showFilterOverlay && (
                    <FilterOverlay
                        id={props.id}
                        colorStyles={colorStyles}
                        options={props.options}
                        groups={props.groups}
                        roles={props.roles}
                        filter={props.filter}
                        setFilter={props.setFilter ?? (() => {})}
                        title={props.title}
                        setPageNo={props.setPageNo}
                        handleApplyFilter={props.handleApplyFilter}
                        handleReset={props.handleReset}
                    />
                )}
                {showSelectionToolbar && props.multiselectAction ? (
                    <div className="gs-list-page-multiselect-action">{props.multiselectAction}</div>
                ) : null}
            </div>
        </div>
    );
}

export default TableHead;
